import type { FotoSerieStand, FotoUploadAntwort } from '../shared/api.js';
import { api, ApiFehler, fehlerText, modus, NetzFehler, token } from './api.js';
import { melde } from './ergebnisse.js';
import { db, protokolliere, type FotoSerie, type WartendesFoto } from './speicher.js';
import { formatiereZeitpunkt, groesseText } from './zeit.js';

/**
 * Foto-Warteschlange wie im Konzept: Fotos liegen mit Kennung und Prüfsumme in
 * IndexedDB, werden einzeln hochgeladen und überleben das Schließen der App.
 * Die Warteschlange läuft in der App, nicht im Service Worker.
 */

export const ereignisse = new EventTarget();

function meldeFortschritt(): void {
  ereignisse.dispatchEvent(new Event('fortschritt'));
}

async function sha256Hex(blob: Blob): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Testfoto mit langer Seite 2048 px wie im Konzept (0,3–0,5 MB). */
async function erzeugeTestfoto(nr: number, anzahl: number): Promise<Blob> {
  const leinwand = document.createElement('canvas');
  leinwand.width = 2048;
  leinwand.height = 1536;
  const k = leinwand.getContext('2d');
  if (!k) throw new Error('Kein Canvas verfügbar.');
  const verlauf = k.createLinearGradient(0, 0, 2048, 1536);
  verlauf.addColorStop(0, `hsl(${(nr * 47) % 360} 40% 35%)`);
  verlauf.addColorStop(1, `hsl(${(nr * 47 + 120) % 360} 45% 60%)`);
  k.fillStyle = verlauf;
  k.fillRect(0, 0, 2048, 1536);
  // Unregelmäßige Flächen und Rauschen, damit die Datei so groß wird wie ein echtes Foto.
  for (let i = 0; i < 900; i++) {
    k.fillStyle = `hsl(${Math.random() * 360} ${30 + Math.random() * 50}% ${20 + Math.random() * 60}%)`;
    k.fillRect(Math.random() * 2048, Math.random() * 1536, 4 + Math.random() * 120, 4 + Math.random() * 90);
  }
  for (let i = 0; i < 22_000; i++) {
    k.fillStyle = `rgba(${(Math.random() * 255) | 0},${(Math.random() * 255) | 0},${(Math.random() * 255) | 0},0.5)`;
    k.fillRect(Math.random() * 2048, Math.random() * 1536, 3, 3);
  }
  k.fillStyle = '#ffffff';
  k.font = 'bold 140px system-ui, sans-serif';
  k.fillText(`Testfoto ${nr} / ${anzahl}`, 120, 760);
  k.font = '60px system-ui, sans-serif';
  k.fillText(formatiereZeitpunkt(Date.now()), 120, 880);
  return new Promise((fertig, fehler) => leinwand.toBlob((b) => (b ? fertig(b) : fehler(new Error('Foto nicht erzeugt'))), 'image/jpeg', 0.8));
}

export interface SerienStand {
  serie: FotoSerie;
  gesendet: number;
  wartend: number;
  fehler: number;
  bytes: number;
}

export async function aktuelleSerie(): Promise<SerienStand | undefined> {
  const serien = (await db.serien.toArray()).sort((a, b) => b.erstelltAm.localeCompare(a.erstelltAm));
  const serie = serien[0];
  if (!serie) return undefined;
  const fotos = await db.fotos.where('serieId').equals(serie.id).toArray();
  return {
    serie,
    gesendet: fotos.filter((f) => f.status === 'gesendet').length,
    wartend: fotos.filter((f) => f.status === 'wartet').length,
    fehler: fotos.filter((f) => f.status === 'fehler').length,
    bytes: fotos.reduce((s, f) => s + f.groesse, 0),
  };
}

let laufend: Promise<void> | undefined;
let erzeugt = { nr: 0, anzahl: 0 };

export function erzeugungsStand(): { nr: number; anzahl: number } {
  return erzeugt;
}

/** Legt eine neue Serie an: erst alle Fotos erzeugen und speichern, dann hochladen. */
export async function starteSerie(anzahl: number, bremse: boolean): Promise<void> {
  const serie: FotoSerie = { id: crypto.randomUUID(), anzahl, modus: modus(), bremse, status: 'erzeugen', erstelltAm: new Date().toISOString(), unterbrechungen: [] };
  // Erst wenn der Server die Serie kennt, lokal anlegen; sonst bliebe sie im Status „erzeugen“ hängen.
  await api('/api/fotos/serien', { json: { id: serie.id, anzahl } });
  await db.serien.put(serie);
  for (let nr = 1; nr <= anzahl; nr++) {
    erzeugt = { nr, anzahl };
    meldeFortschritt();
    const bild = await erzeugeTestfoto(nr, anzahl);
    const foto: WartendesFoto = { id: crypto.randomUUID(), serieId: serie.id, nr, bild, groesse: bild.size, pruefsumme: await sha256Hex(bild), status: 'wartet', versuche: 0 };
    await db.fotos.put(foto);
  }
  erzeugt = { nr: 0, anzahl: 0 };
  await db.serien.update(serie.id, { status: 'laeuft', gestartetAm: new Date().toISOString() });
  meldeFortschritt();
  void arbeite();
}

/**
 * Beim Start der App: Lief eine Serie noch, wurde die App mitten im Hochladen
 * geschlossen oder neu geladen. Das wird vermerkt, dann geht es von selbst weiter.
 */
export async function beimStart(): Promise<void> {
  for (const serie of await db.serien.where('status').anyOf('laeuft', 'erzeugen').toArray()) {
    if (serie.status === 'erzeugen') {
      // Beim Erzeugen unterbrochen: Serie verwerfen, sie ist kein Upload-Test.
      await db.fotos.where('serieId').equals(serie.id).delete();
      await db.serien.update(serie.id, { status: 'abgebrochen' });
      continue;
    }
    const wartend = await db.fotos.where({ serieId: serie.id, status: 'wartet' }).count();
    if (wartend === 0) continue;
    const gesendet = await db.fotos.where({ serieId: serie.id, status: 'gesendet' }).count();
    const unterbrechungen = [...serie.unterbrechungen, { zeit: new Date().toISOString(), gesendet }];
    await db.serien.update(serie.id, { unterbrechungen });
    await protokolliere('foto-unterbrechung', { serie: serie.id, gesendet });
  }
  meldeFortschritt();
  void arbeite();
}

/** Lädt alle wartenden Fotos hoch; läuft höchstens einmal gleichzeitig. */
export function arbeite(): Promise<void> {
  laufend ??= abarbeiten().finally(() => {
    laufend = undefined;
    meldeFortschritt();
  });
  return laufend;
}

const warte = (ms: number) => new Promise((fertig) => window.setTimeout(fertig, ms));

async function abarbeiten(): Promise<void> {
  for (const serie of await db.serien.where('status').equals('laeuft').toArray()) {
    let netzFehler = 0;
    for (;;) {
      const naechstes = (await db.fotos.where({ serieId: serie.id, status: 'wartet' }).sortBy('nr'))[0];
      if (!naechstes) break;
      try {
        await ladeHoch(serie, naechstes);
        netzFehler = 0;
      } catch (fehler) {
        if (fehler instanceof NetzFehler) {
          // Kein Netz: später weiter, nicht von vorne. Nach einigen Versuchen bis zum nächsten „online“ warten.
          netzFehler += 1;
          if (netzFehler >= 5) return;
          await warte(3_000);
          continue;
        }
        const versuche = naechstes.versuche + 1;
        // Ein fehlerhaftes Foto blockiert nie den Rest: nach 3 Versuchen als Fehler markieren.
        await db.fotos.update(naechstes.id, { versuche, letzterFehler: fehlerText(fehler), status: versuche >= 3 ? 'fehler' : 'wartet' });
        await protokolliere('foto-fehler', { foto: naechstes.id, fehler: fehlerText(fehler) });
      }
      meldeFortschritt();
    }
    await werteAus(serie.id);
  }
}

async function ladeHoch(serie: FotoSerie, foto: WartendesFoto): Promise<void> {
  if (!foto.bild) throw new Error('Foto fehlt im Speicher.');
  const t = await token(serie.modus);
  const kopf: Record<string, string> = {
    'content-type': 'image/jpeg',
    'x-serie': serie.id,
    'x-nr': String(foto.nr),
    'x-pruefsumme': foto.pruefsumme,
    'x-bremse': serie.bremse ? '1' : '0',
  };
  if (t) kopf.authorization = `Bearer ${t}`;
  let antwort: Response;
  try {
    antwort = await fetch(`/api/fotos/${foto.id}`, { method: 'PUT', headers: kopf, body: foto.bild, cache: 'no-store', signal: AbortSignal.timeout(60_000) });
  } catch {
    throw new NetzFehler();
  }
  if (!antwort.ok) {
    const daten = (await antwort.json().catch(() => undefined)) as { fehler?: string } | undefined;
    throw new ApiFehler(antwort.status, daten?.fehler ?? `Fehler ${antwort.status}`);
  }
  const ergebnis = (await antwort.json()) as FotoUploadAntwort;
  // Nach bestätigtem Upload das Bild lokal freigeben; Kennung und Prüfsumme bleiben für den Abgleich.
  await db.fotos.update(foto.id, { status: 'gesendet', bild: null, versuche: foto.versuche + 1 });
  if (ergebnis.status === 'bereits-vorhanden') await protokolliere('foto-erneut', { foto: foto.id });
}

/** Abgleich mit dem Server: genau alle Fotos, keines doppelt, jede Prüfsumme richtig. */
async function werteAus(serieId: string): Promise<void> {
  const serie = await db.serien.get(serieId);
  if (!serie) return;
  const lokal = await db.fotos.where('serieId').equals(serieId).toArray();
  if (lokal.some((f) => f.status === 'wartet')) return;
  let stand: FotoSerieStand;
  try {
    stand = await api<FotoSerieStand>(`/api/fotos/serien/${serieId}`, { modus: serie.modus });
  } catch {
    return;
  }
  const server = new Map(stand.fotos.map((f) => [f.id, f]));
  const fehlend = lokal.filter((f) => !server.has(f.id)).length;
  const falsch = lokal.filter((f) => server.has(f.id) && server.get(f.id)?.pruefsumme !== f.pruefsumme).length;
  const fremd = stand.fotos.filter((f) => !lokal.some((l) => l.id === f.id)).length;
  const alleDa = stand.gespeichert === serie.anzahl && fehlend === 0 && falsch === 0 && fremd === 0;
  const unterbrochen = serie.unterbrechungen.length > 0;
  const fertigAm = new Date().toISOString();
  const details = {
    anzahl: serie.anzahl,
    gespeichert: stand.gespeichert,
    erneutGesendet: stand.erneutGesendet,
    fehlend,
    falschePruefsumme: falsch,
    unterbrechungen: serie.unterbrechungen,
    dauerSek: serie.gestartetAm ? Math.round((Date.parse(fertigAm) - Date.parse(serie.gestartetAm)) / 1000) : null,
    groesse: groesseText(lokal.reduce((s, f) => s + f.groesse, 0)),
    bremse: serie.bremse,
  };
  const ersteUnterbrechung = serie.unterbrechungen[0];
  if (!alleDa) {
    await melde('foto-warteschlange', 'fehler', `Nur ${stand.gespeichert} von ${serie.anzahl} Fotos richtig angekommen (fehlend ${fehlend}, falsche Prüfsumme ${falsch}).`, details);
  } else if (unterbrochen && ersteUnterbrechung) {
    await melde(
      'foto-warteschlange',
      'ok',
      `${serie.anzahl} von ${serie.anzahl} Fotos angekommen, keines doppelt. Nach Abbruch bei Foto ${ersteUnterbrechung.gesendet + 1} fortgesetzt.`,
      details,
    );
  } else {
    await melde('foto-warteschlange', 'offen', `${serie.anzahl} von ${serie.anzahl} Fotos angekommen, aber ohne Abbruch. Für den Test die App während des Hochladens schließen.`, details);
  }
  await db.serien.update(serieId, { status: 'fertig', fertigAm });
}
